import React, { useEffect, useState } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { registerPushAndVoip } from '../utils/pushNotifications';

const PermissionGate = ({ children }) => {
    const [status, setStatus] = useState('checking'); // checking, required, granted
    const { showSuccess, showError } = useNotification();
    const { token } = useAuth(); // Only prompt after login

    useEffect(() => {
        if (Capacitor.getPlatform() === 'web' || !token) {
            setStatus('granted');
            return;
        }
        checkPermissions();
    }, [token]);

    const checkPermissions = async () => {
        try {
            console.log('🛡️ PermissionGate: Checking push permissions...');
            const pushStatus = await PushNotifications.checkPermissions();

            // 'prompt' means we haven't asked yet. 'granted' means we have it. 'denied' means user said no.
            if (pushStatus.receive === 'prompt') {
                setStatus('required');
            } else {
                setStatus('granted');
            }
        } catch (error) {
            console.error('Error checking permissions:', error);
            setStatus('granted'); // Fallback to let them in
        }
    };

    const requestPushPermissions = async () => {
        try {
            const pushReq = await PushNotifications.requestPermissions();

            if (pushReq.receive === 'granted') {
                showSuccess('Notifications enabled!');
                await registerPushAndVoip();
            } else {
                showError('Notifications disabled. You will miss incoming calls.');
            }
            setStatus('granted');
        } catch (error) {
            showError('Failed to request permissions');
            setStatus('granted');
        }
    };

    if (status === 'checking') return null;

    if (status === 'required') {
        return (
            <div className="fixed inset-0 bg-dark z-[9999] flex items-center justify-center p-6 text-center">
                <div className="max-w-md w-full">
                    <div className="w-24 h-24 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gold/30">
                        <svg className="w-12 h-12 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-4">Stay Connected</h2>
                    <p className="text-gray-400 mb-8 leading-relaxed">
                        To receive incoming calls from your doctors and patients, Dr. Vivara needs permission to send you <strong>Notifications</strong>.
                    </p>
                    <div className="space-y-4">
                        <button
                            onClick={requestPushPermissions}
                            className="w-full bg-gold text-dark font-bold py-4 rounded-xl shadow-lg shadow-gold/20 active:scale-95 transition-all"
                        >
                            Enable Notifications
                        </button>
                        <button
                            onClick={() => setStatus('granted')}
                            className="w-full text-gray-500 text-sm font-medium py-2"
                        >
                            Maybe Later
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return children;
};

export default PermissionGate;
